import React, { useState, useEffect, useContext, ReactNode } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

import { UserContext, UserContextType } from './UserContext';
import { ThemeContext } from './ThemeContext';
import {
  ThemeColors,
  FontSizePresets,
  FONT_SIZE_DEFAULT,
  FontSizePreference,
} from '#types/System.Themes.types';

const COLORS = ThemeColors;

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { settings } = useContext<UserContextType>(UserContext);
  const preferDarkMode = settings?.preferDarkMode || 'system';
  const fontSizePreference =
    (settings?.fontSize as FontSizePreference) || FONT_SIZE_DEFAULT;
  const baseFontSize =
    FontSizePresets[fontSizePreference] ?? FontSizePresets[FONT_SIZE_DEFAULT];
  const [systemDarkMode, setSystemDarkMode] = useState<boolean>(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    const matcher = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      setSystemDarkMode(e.matches);
    };

    matcher.addEventListener('change', onChange);
    return () => matcher.removeEventListener('change', onChange);
  }, []);

  const darkMode =
    preferDarkMode === 'dark'
      ? true
      : preferDarkMode === 'light'
        ? false
        : systemDarkMode;

  useEffect(() => {
    document.documentElement.style.scrollbarColor =
      'gray ' + (darkMode ? COLORS.APP_BG.DARK : COLORS.APP_BG.LIGHT);
  }, [darkMode]);

  const currentTheme = {
    ...(darkMode ? { algorithm: antdTheme.darkAlgorithm } : {}),
    token: {
      colorText: darkMode ? COLORS.WHITE_ALPHA.P95 : COLORS.BLACK_ALPHA.P85,
      fontFamily: "'Work Sans', sans-serif",
      colorTide: darkMode ? COLORS.TIDE.DARK : COLORS.TIDE.LIGHT,
      colorTideWash: darkMode ? COLORS.TIDE_WASH.DARK : COLORS.TIDE_WASH.LIGHT,
      colorTideDeep: darkMode ? COLORS.TIDE_DEEP.DARK : COLORS.TIDE_DEEP.LIGHT,
      colorBorderDraft: darkMode
        ? COLORS.DRAFT_BORDER.DARK
        : COLORS.DRAFT_BORDER.LIGHT,
      colorPrimary: darkMode ? COLORS.PRIMARY.DARK : COLORS.PRIMARY.LIGHT,
      colorPrimaryBorder: darkMode ? COLORS.PRIMARY.DARK : COLORS.PRIMARY.LIGHT,
      colorInfo: darkMode ? COLORS.PRIMARY.DARK : COLORS.PRIMARY.LIGHT,
      colorBgElevated: darkMode
        ? COLORS.FIELD_SELECT.DARK
        : COLORS.FIELD_SELECT.LIGHT,
      colorFillTertiary: darkMode
        ? COLORS.FIELD_BG.DARK
        : COLORS.FIELD_BG.LIGHT,
      fontSize: baseFontSize,
      fontSizeHEading3: Math.round(baseFontSize * 1.75),
      fontSizeHeading4: Math.round(baseFontSize * 1.5),
      fontSizeHeading5: Math.round(baseFontSize * 1.25),
      lineHeightHeading5: 1,
      borderRadius: 4,
      borderRadiusLG: 4,
      fontWeightStrong: 600,
      customBgTransparent: darkMode
        ? COLORS.TRANSPARENT_BG.DARK
        : COLORS.TRANSPARENT_BG.LIGHT,
      colorTextDisabled: darkMode
        ? COLORS.WHITE_ALPHA.P68
        : COLORS.BLACK_ALPHA.P68,
      colorLinkBgHover: darkMode ? COLORS.PRIMARY.LIGHT : COLORS.PRIMARY.DARK,
      colorBgHover: darkMode ? COLORS.PRIMARY.LIGHT : COLORS.PRIMARY.DARK,
      colorPrimaryBgHover: darkMode
        ? COLORS.PRIMARY.LIGHT
        : COLORS.PRIMARY.DARK,
      colorError: COLORS.SOLID.RED,
      colorFillQuaternary: darkMode
        ? COLORS.FIELD_ALT.DARK
        : COLORS.FIELD_ALT.LIGHT,
      paddingXXS: 2,
      paddingXS: 5,
      paddingSM: 10,
      padding: 20,
      paddingMD: 20,
      paddingLG: 25,
      paddingXL: 30,
      marginXXS: 2,
      marginXS: 5,
      marginSM: 10,
      margin: 20,
      marginMD: 20,
      marginLG: 25,
      marginXL: 35,
      marginXXL: 45,
    },
    components: {
      Button: {
        controlTmpOutline: COLORS.SOLID.RED,
        controlOutlineWidth: 0,
        boxShadowTertiary: 'none',
        colorBgContainer: darkMode
          ? COLORS.PRIMARY_L.DARK
          : COLORS.PRIMARY_L.LIGHT,
        defaultBg: darkMode ? COLORS.PRIMARY_XL.DARK : COLORS.PRIMARY_XL.LIGHT,
        defaultColor: darkMode ? COLORS.PRIMARY.DARK : COLORS.PRIMARY.LIGHT,
        defaultBorderColor: 'transparent',
        colorText: darkMode ? COLORS.WHITE_ALPHA.P85 : COLORS.BLACK_ALPHA.P85,
        colorBorder: darkMode ? COLORS.PRIMARY.DARK : COLORS.PRIMARY.LIGHT,
        borderColorDisabled: darkMode
          ? COLORS.FIELD_SELECT.DARK
          : COLORS.FIELD_SELECT.LIGHT,
        linkHoverBg: darkMode ? COLORS.PRIMARY_L.DARK : COLORS.PRIMARY_L.LIGHT,
        colorTextLightSolid: darkMode
          ? COLORS.BLACK_ALPHA.P60
          : COLORS.WHITE_ALPHA.P95,
        colorTextDefault: darkMode
          ? COLORS.WHITE_ALPHA.P95
          : COLORS.BLACK_ALPHA.P85,
        fontSizeSM: 12,
      },
      Segmented: {
        itemSelectedBg: darkMode
          ? COLORS.FIELD_SELECT.DARK
          : COLORS.FIELD_SELECT.LIGHT,
        trackBg: darkMode ? COLORS.FIELD_BG.DARK : COLORS.FIELD_BG.LIGHT,
        trackPadding: 3,
      },
      Radio: {
        buttonCheckedBg: darkMode
          ? COLORS.PRIMARY_XL.DARK
          : COLORS.PRIMARY_XL.LIGHT,
      },
      Flex: {
        wrap: 'flex-wrap',
      },
      Form: {
        verticalLabelPadding: 0,
        itemMarginBottom: 10,
      },
      Cascader: {
        dropdownHeight: 400,
      },
      Table: {
        filterDropdownBg: 'transparent',
        borderColor: darkMode ? COLORS.FIELD_ALT.DARK : COLORS.FIELD_ALT.LIGHT,
        headerBg: darkMode ? COLORS.FIELD_ALT.DARK : COLORS.FIELD_ALT.LIGHT,
        headerSplitColor: 'transparent',
        rowHoverBg: darkMode
          ? COLORS.FIELD_SELECT.DARK
          : COLORS.FIELD_SELECT.LIGHT,
        footerBg: darkMode ? COLORS.FIELD_SELECT.DARK : COLORS.FIELD_ALT.LIGHT,
        cellPaddingBlock: 5,
        cellPaddingInline: 10,
      },
      Input: {
        colorText:
          (darkMode ? COLORS.WHITE_ALPHA.P95 : COLORS.BLACK_ALPHA.P85) +
          ' !important', // this seems to be needed due to an antd bug (?)
      },
      Timeline: {
        dotBg: darkMode ? COLORS.FIELD_SELECT.DARK : COLORS.FIELD_SELECT.LIGHT,
      },
      Descriptions: {
        titleMarginBottom: 0,
      },
      Typography: {
        titleMarginTop: 5,
        titleMarginBottom: 5,
      },
    },
  };

  return (
    <ThemeContext.Provider value={{ darkMode }}>
      <ConfigProvider theme={currentTheme}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};
